import React, { ReactElement, useEffect, useState } from 'react';
import Layout from 'layout/Layout';
import Login from 'login/Login';
import {
    LoginErrorState,
    LoginErrorType,
    LoginStatus,
} from 'login/store/LoginState';
import {
    useDispatchLoginAction,
    useDispatchLoginResetAction,
} from 'login/store/dispatcher';
import { useLoginStateSelector } from 'login/store/selectors';
import { AppContextProvider } from './AppContext';
import { WellKnownLayoutOrigin } from 'layout/WellKnownLayoutOrigin';
import { useDispatchSetAppMode } from 'app/store/general/dispatcher';
import { AppMode } from './store/general/AppMode';
import Services from 'store/Services';
import {
    useAvailabilityStatus,
    useNetworkConnectivityStatus,
} from 'app/store/connectivity/selectors';
import { buildFromWebsocketErrorCode } from 'communication/streaming/StreamingErrorText';
import { availabilyStatusDesc } from 'communication/streaming/StreamStatusMessage';

export interface StandaloneAppProps {
    layout: string;
    services: Services;
}

export default function StandaloneApp(props: StandaloneAppProps): ReactElement {
    const loginState = useLoginStateSelector();
    const { error, status: loginStatus } = loginState;
    const dispatchLogin = useDispatchLoginAction();
    const dispatchLoginReset = useDispatchLoginResetAction();
    const dispatchSetAppMode = useDispatchSetAppMode();
    const { layout: layoutProps, services } = props;
    const { layoutService, loggingService } = services;

    const [layout, setLayout] = useState<string | undefined>();

    const networkConnectivityStatus = useNetworkConnectivityStatus();
    const lostNetworkConnection =
        networkConnectivityStatus.lostConnectionStatus;
    const availabilityStatus = useAvailabilityStatus();

    useEffect(() => {
        dispatchSetAppMode(AppMode.STANDALONE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loginStatus !== LoginStatus.SUCCESS) {
            return;
        }
        if (layout) {
            return;
        }
        if (layoutProps && layoutProps !== 'default') {
            setLayout(layoutProps);
            return;
        }

        layoutService
            .getLastLoadedLayout()
            .then((lastLoaded) => {
                const layoutToLoad = lastLoaded || layoutProps;
                loggingService.debug(
                    'StandaloneApp',
                    'Loading layout ' + layoutToLoad,
                    { lastLoaded, layoutProps }
                );
                setLayout(layoutToLoad);
            })
            .catch((err) => {
                loggingService.critical(
                    'StandaloneApp',
                    'Error getting last layout',
                    err
                );
                setLayout(layoutProps);
            });
    }, [
        layout,
        layoutProps,
        loginStatus,
        setLayout,
        loggingService,
        layoutService,
    ]);

    const indigoUnavailable =
        availabilityStatus && !availabilityStatus?.available;

    const loginProps: {
        loginDisabled?: boolean;
        error?: LoginErrorState;
        showContactSupport?: boolean;
        onSubmit: (username: string, password: string) => void;
        onReset: () => void;
    } = {
        onSubmit: dispatchLogin,
        onReset: dispatchLoginReset,
        showContactSupport: true,
        loginDisabled: false,
    };
    let showLogin = false;

    if (indigoUnavailable) {
        showLogin = true;
        loginProps.loginDisabled = true;
        loginProps.error = {
            type: LoginErrorType.LOST_NETWORK_CONNECTION,
            message: `Indigo backend is unavailable (${availabilyStatusDesc(
                availabilityStatus?.status
            )})`,
            innerError: {},
        };
    }
    if (!indigoUnavailable && lostNetworkConnection) {
        showLogin = true;
        loginProps.error = mapNetworkDisconnectedError(
            lostNetworkConnection.errorCode
        );
    }
    if (
        !indigoUnavailable &&
        !lostNetworkConnection &&
        loginStatus !== LoginStatus.SUCCESS
    ) {
        showLogin = true;
        loginProps.error = error;
    }

    return (
        <AppContextProvider services={services}>
            <div className="App">
                {showLogin && <Login {...loginProps} />}

                {!indigoUnavailable &&
                    loginStatus === LoginStatus.SUCCESS &&
                    layout && (
                        <Layout
                            origin={WellKnownLayoutOrigin.STANDALONE}
                            layout={layout}
                        />
                    )}
            </div>
        </AppContextProvider>
    );
}

function mapNetworkDisconnectedError(
    errorCode?: number,
    reloadPossible = false
): LoginErrorState {
    const errorText = buildFromWebsocketErrorCode(errorCode, reloadPossible);
    return {
        message: errorText.title,
        type: LoginErrorType.SYSTEM_ERROR,
        innerError: undefined,
    };
}
